import type { MappedRoleOption } from '@/types/roles';
import type { TFunction } from 'i18next';

import { ALL } from '@/constants';
import { ROLES } from '@/utils/constants/roles';

const ROLE_ID_BY_KEY: Record<string, number> = {};
const ROLE_KEY_BY_ID: Record<number, string> = {};

for (const [key, role] of Object.entries(ROLES)) {
  ROLE_ID_BY_KEY[key] = role.id;
  ROLE_KEY_BY_ID[role.id] = key;
}

export function hasAllRoles(roleKeys: string[], allRoleKeys: string[]): boolean {
  const roleKeySet = new Set(roleKeys);

  return allRoleKeys.every((key) => roleKeySet.has(key));
}

export function collapseRolesToAll(roleKeys: string[], allRoleKeys: string[]): string[] {
  return hasAllRoles(roleKeys, allRoleKeys) ? [ALL] : roleKeys;
}

export function getRoleDisplayName(shortName: string, t: TFunction): string {
  return shortName === ROLES.all.shortName
    ? t('common:configuration.predefinedMessages.all')
    : shortName;
}

function normalizeRoleToken(value: string): string {
  return value.trim().toLowerCase();
}

function resolveRoleKey(value: string): string | null {
  const normalized = normalizeRoleToken(value);

  if (ROLES[normalized as keyof typeof ROLES]) {
    return normalized;
  }

  return null;
}

function normalizeRoleCode(value: string | number): string | null {
  if (typeof value === 'number') {
    const roleKey = ROLE_KEY_BY_ID[value];

    return roleKey ?? null;
  }

  const trimmed = value.trim();

  if (/^\d+$/.test(trimmed)) {
    const parsed = Number.parseInt(trimmed, 10);
    const roleKey = ROLE_KEY_BY_ID[parsed];

    return roleKey ?? null;
  }

  const roleKey = resolveRoleKey(trimmed);

  return roleKey;
}

export function normalizeRoleCodeArray(values: Array<string | number>): string[] {
  return values.reduce<string[]>((acc, value) => {
    const code = normalizeRoleCode(value);

    if (code !== null) {
      acc.push(code);
    }

    return acc;
  }, []);
}

export function mapRoleIdentifiersToShortNames(roles: Array<string | number>): string[] {
  return roles.reduce<string[]>((acc, role) => {
    const roleKey =
      typeof role === 'number' ? (ROLE_KEY_BY_ID[role] ?? null) : resolveRoleKey(role);

    const shortName = roleKey ? ROLES[roleKey as keyof typeof ROLES]?.shortName : null;

    if (shortName) {
      acc.push(shortName);
    }

    return acc;
  }, []);
}

export function createMappedRoleShortName(roles: MappedRoleOption[]): Map<string, string> {
  return new Map(roles.map(({ key, shortName }) => [key, shortName]));
}

export const recipientIdToRole: Record<string, string> = (() => {
  const map: Record<string, string> = { [ALL]: 'all' };
  for (const [key, { shortName }] of Object.entries(ROLES)) {
    if (key === 'all') {
      continue;
    }

    map[shortName.toLowerCase()] = key;
    map[key] = key;
  }

  return map;
})();

export function mapRoleKeysToLowercaseShortNameIds(roleKeys: string[]): string[] {
  return roleKeys.flatMap((value) => {
    const roleCode = String(value).trim().toLowerCase();

    if (!roleCode) {
      return [];
    }

    if (roleCode === ALL) {
      return [ALL];
    }

    const role = ROLES[roleCode as keyof typeof ROLES];

    return role ? [role.shortName.toLowerCase()] : [];
  });
}
